import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  Button,
  TextField,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout/Layout";
import moment from "moment";
import { config, baseURL } from "../../config";
import { MaterialReactTable } from "material-react-table";
import * as XLSX from "xlsx/xlsx.mjs";
import DescriptionIcon from "@mui/icons-material/Description";

// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PaidIcon from "@mui/icons-material/Paid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

function YearlyBonus() {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bonuses, setBonuses] = useState([]);

  // Filter states
  const [filterCompany, setFilterCompany] = useState("0");
  const [filterFromYear, setFilterFromYear] = useState("");
  const [filterToYear, setFilterToYear] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [companyEmployeesCount, setCompanyEmployeesCount] = useState(0);

  // Add dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [formCompany, setFormCompany] = useState("");
  const [formEmployee, setFormEmployee] = useState("");
  const [formBonusFromYear, setFormBonusFromYear] = useState(moment().year());
  const [formBonusToYear, setFormBonusToYear] = useState(moment().year() + 1);
  const [formAmount, setFormAmount] = useState("");
  const [formDate, setFormDate] = useState(moment().format("YYYY-MM-DD"));
  const [formRemarks, setFormRemarks] = useState("");

  const [alertMsg, setAlertMsg] = useState(null);
  const [editRecordId, setEditRecordId] = useState(null);

  // Load companies
  useEffect(() => {
    axios({
      ...config,
      method: "POST",
      url: `${baseURL}/manage-companies`,
      data: JSON.stringify({ intType: 5 }),
    })
      .then((res) => {
        setCompanies(res.data.result || []);
      })
      .catch((err) => console.error("Error loading companies", err));
  }, []);

  // Fetch bonus list
  const fetchBonuses = () => {
    setLoading(true);
    axios({
      method: "POST",
      url: `${baseURL}/yearly-bonus/list`,
      headers: { "Content-Type": "application/json" },
      data: JSON.stringify({
        company_id: filterCompany === "0" ? null : filterCompany,
      }),
    })
      .then((res) => {
        setBonuses(res.data.result || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching bonuses list", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBonuses();
  }, [filterCompany]);

  // Load active employees list for selected filterCompany to calculate Pending count
  useEffect(() => {
    axios({
      method: "GET",
      url: `${baseURL}/yearly-bonus/employees/${filterCompany}`,
    })
      .then((res) => {
        setCompanyEmployeesCount(res.data.result?.length || 0);
      })
      .catch((err) => console.error("Error loading active company employees", err));
  }, [filterCompany]);

  // Load employees when form company selection changes
  useEffect(() => {
    if (!formCompany) {
      setEmployees([]);
      return;
    }
    axios({
      method: "GET",
      url: `${baseURL}/yearly-bonus/employees/${formCompany}`,
    })
      .then((res) => {
        setEmployees(res.data.result || []);
      })
      .catch((err) => console.error("Error loading company employees", err));
  }, [formCompany]);

  const handleAddBonus = (e) => {
    e.preventDefault();
    if (!formEmployee || !formCompany || !formBonusFromYear || !formBonusToYear || !formAmount || !formDate) {
      setAlertMsg({ type: "error", text: "Please fill all required fields" });
      return;
    }

    const isEdit = editRecordId !== null;

    axios({
      method: isEdit ? "PUT" : "POST",
      url: isEdit ? `${baseURL}/yearly-bonus/${editRecordId}` : `${baseURL}/yearly-bonus`,
      headers: { "Content-Type": "application/json" },
      data: JSON.stringify({
        employee_id: formEmployee,
        company_id: formCompany,
        from_year: parseInt(formBonusFromYear),
        to_year: parseInt(formBonusToYear),
        bonus_amount: parseFloat(formAmount),
        payment_date: formDate,
        remarks: formRemarks,
      }),
    })
      .then((res) => {
        if (res.data.success) {
          setOpenAddDialog(false);
          // reset form
          setFormCompany("");
          setFormEmployee("");
          setFormBonusFromYear(moment().year());
          setFormBonusToYear(moment().year() + 1);
          setFormAmount("");
          setFormRemarks("");
          setFormDate(moment().format("YYYY-MM-DD"));
          setEditRecordId(null);
          fetchBonuses();
        } else {
          setAlertMsg({ type: "error", text: res.data.message });
        }
      })
      .catch((err) => {
        console.error("Error saving bonus", err);
        setAlertMsg({ type: "error", text: "Failed to record bonus" });
      });
  };

  const handleOpenEditDialog = (record) => {
    setAlertMsg(null);
    setEditRecordId(record.id);
    setFormCompany(record.company_id);
    setFormEmployee(record.employee_id);
    setFormBonusFromYear(record.from_year);
    setFormBonusToYear(record.to_year);
    setFormAmount(record.bonus_amount);
    setFormDate(moment(record.payment_date).format("YYYY-MM-DD"));
    setFormRemarks(record.remarks || "");
    setOpenAddDialog(true);
  };

  const handleDeleteBonus = (id) => {
    if (!window.confirm("Are you sure you want to delete this bonus entry?")) return;
    axios({
      method: "DELETE",
      url: `${baseURL}/yearly-bonus/${id}`,
    })
      .then((res) => {
        if (res.data.success) {
          fetchBonuses();
        }
      })
      .catch((err) => console.error("Error deleting bonus", err));
  };

  // KPI Calculations
  const displayedBonuses = bonuses.filter((b) => {
    if (filterFromYear && b.from_year.toString() !== filterFromYear) {
      return false;
    }
    if (filterToYear && b.to_year.toString() !== filterToYear) {
      return false;
    }
    return true;
  });

  const totalPaid = displayedBonuses.reduce((acc, curr) => acc + parseFloat(curr.bonus_amount || 0), 0);
  const uniqueEmployees = new Set(displayedBonuses.map((b) => b.employee_id)).size;
  const pendingEmployees = Math.max(0, companyEmployeesCount - uniqueEmployees);

  const currentYear = moment().year();
  const yearsList = [];
  for (let y = currentYear - 5; y <= currentYear + 5; y++) {
    yearsList.push(y.toString());
  }

  const bonusYearsList = [];
  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
    bonusYearsList.push(`${y}-${y + 1}`);
  }

  const handleExportExcel = (table) => {
    const rows = table.getFilteredRowModel().rows;
    const exportData = rows.map((row, index) => {
      const b = row.original;
      return {
        "S.No": index + 1,
        "Employee Name": b.employee_name,
        "Company": b.company_name_str,
        "Bonus Year": `${b.from_year}-${b.to_year}`,
        "Payment Date": moment(b.payment_date).format("DD-MM-YYYY"),
        "Amount (Rs.)": parseFloat(b.bonus_amount),
        "Remarks": b.remarks || "-",
      };
    });

    const firstRowBonus = rows[0]?.original;
    const yearStr = (filterFromYear && filterToYear) ? `${filterFromYear}-${filterToYear}` : (firstRowBonus ? `${firstRowBonus.from_year}-${firstRowBonus.to_year}` : `${currentYear}-${currentYear + 1}`);

    const workSheet = XLSX.utils.json_to_sheet(exportData, { origin: "A2" });
    const sheetTitle = "Yearly Bonus of the year " + yearStr;
    XLSX.utils.sheet_add_aoa(workSheet, [[sheetTitle]], { origin: "A1" });

    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Yearly Bonuses");
    
    const fileName = `Yearly Bonus of the year ${yearStr}.xlsx`;
    XLSX.writeFile(workBook, fileName);
  };

  // MaterialReactTable Columns definition
  const columns = [
    {
      id: "sNo",
      header: "S.No",
      size: 80,
      Cell: ({ row }) => row.index + 1,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "employee_name",
      header: "Employee Name",
      size: 200,
    },
    {
      accessorKey: "company_name_str",
      header: "Company",
      size: 200,
    },
    {
      accessorFn: (row) => `${row.from_year}-${row.to_year}`,
      id: "bonus_year",
      header: "Bonus Year",
      size: 120,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorFn: (row) => moment(row.payment_date).format("DD-MM-YYYY"),
      id: "payment_date",
      header: "Payment Date",
      size: 150,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorFn: (row) => `₹ ${parseFloat(row.bonus_amount).toLocaleString()}`,
      id: "bonus_amount",
      header: "Amount (Rs.)",
      size: 150,
      muiTableHeadCellProps: { align: "right" },
      muiTableBodyCellProps: { align: "right" },
    },
    {
      accessorKey: "remarks",
      header: "Remarks",
      size: 250,
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      id: "actions",
      header: "Action",
      size: 100,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <Tooltip title="Update Record">
          <IconButton color="primary" onClick={() => handleOpenEditDialog(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout>
      <Box sx={{ padding: "20px 25px", pt: "95px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        {/* Header */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h4" className="text-bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CardGiftcardIcon fontSize="large" color="primary" /> Yearly Bonus Management
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditRecordId(null);
                setFormCompany("");
                setFormEmployee("");
                setFormBonusFromYear(moment().year());
                setFormBonusToYear(moment().year() + 1);
                setFormAmount("");
                setFormRemarks("");
                setFormDate(moment().format("YYYY-MM-DD"));
                setAlertMsg(null);
                setOpenAddDialog(true);
              }}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: "bold",
                px: 3,
              }}
            >
              Record Bonus Payment
            </Button>
          </Grid>
        </Grid>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 3, borderRadius: 2, display: "flex", alignItems: "center", gap: 2, boxShadow: 3 }}>
              <Box sx={{ p: 1.5, backgroundColor: "#E3F2FD", borderRadius: "50%", display: "flex" }}>
                <CardGiftcardIcon color="primary" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" className="text-bold">
                  Total Bonuses Paid
                </Typography>
                <Typography variant="h5" className="text-bold">
                  ₹ {totalPaid.toLocaleString()}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 3, borderRadius: 2, display: "flex", alignItems: "center", gap: 2, boxShadow: 3 }}>
              <Box sx={{ p: 1.5, backgroundColor: "#FFE0B2", borderRadius: "50%", display: "flex" }}>
                <PeopleAltIcon sx={{ color: "#FF9800" }} fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" className="text-bold">
                  Pending Employees
                </Typography>
                <Typography variant="h5" className="text-bold">
                  {pendingEmployees}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 3, borderRadius: 2, display: "flex", alignItems: "center", gap: 2, boxShadow: 3 }}>
              <Box sx={{ p: 1.5, backgroundColor: "#E8F5E9", borderRadius: "50%", display: "flex" }}>
                <PeopleAltIcon color="success" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" className="text-bold">
                  Employees Awarded
                </Typography>
                <Typography variant="h5" className="text-bold">
                  {uniqueEmployees}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Filter Company
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="0">All Companies</MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                From Year
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="e.g. 2026"
                value={filterFromYear}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterFromYear(val);
                  if (val && val.length === 4) {
                    setFilterToYear((parseInt(val) + 1).toString());
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                To Year
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="e.g. 2027"
                value={filterToYear}
                onChange={(e) => setFilterToYear(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Global Search
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search name, company, amount, remarks..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </Grid>
          </Grid>
        </Card>

        {/* Material React Table */}
        <MaterialReactTable
          columns={columns}
          data={displayedBonuses}
          state={{ globalFilter, isLoading: loading }}
          onGlobalFilterChange={setGlobalFilter}
          enableGlobalFilter={true}
          enableColumnFilters={true}
          enablePagination={true}
          enableSorting={true}
          enableFullScreenToggle={false}
          enableStickyHeader
          enableTopToolbar={true}
          initialState={{ showGlobalFilter: false }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: "flex", gap: "1rem", p: "0.5rem", flexWrap: "wrap" }}>
              <Button
                onClick={() => handleExportExcel(table)}
                style={{ background: "#1a73e829" }}
                startIcon={<DescriptionIcon />}
                sx={{ px: 4, textTransform: "none", fontWeight: "bold" }}
              >
                Export to Excel
              </Button>
            </Box>
          )}
          muiTableContainerProps={{
            sx: { maxHeight: "550px" },
          }}
          muiTablePaperProps={{
            elevation: 2,
            sx: {
              borderRadius: "8px",
            },
          }}
        />

        {/* Add Dialog */}
        <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleAddBonus}>
            <DialogTitle className="text-bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CardGiftcardIcon color="primary" /> {editRecordId ? "Update Yearly Bonus Payment" : "Record Yearly Bonus Payment"}
            </DialogTitle>
            <DialogContent dividers>
              {alertMsg && (
                <Alert severity={alertMsg.type} sx={{ mb: 2 }}>
                  {alertMsg.text}
                </Alert>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Company *
                  </Typography>
                  <FormControl fullWidth size="small" required>
                    <Select
                      value={formCompany}
                      onChange={(e) => {
                        setFormCompany(e.target.value);
                        setFormEmployee("");
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Company
                      </MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.company_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Employee *
                  </Typography>
                  <FormControl fullWidth size="small" required disabled={!formCompany}>
                    <Select
                      value={formEmployee}
                      onChange={(e) => setFormEmployee(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {formCompany ? "Select Employee" : "Select a company first"}
                      </MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.employee_name} ({emp.employee_no || "No Code"})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    From Year *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    required
                    placeholder="e.g. 2026"
                    value={formBonusFromYear}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || "";
                      setFormBonusFromYear(val);
                      if (val) {
                        setFormBonusToYear(val + 1);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    To Year *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    required
                    placeholder="e.g. 2027"
                    value={formBonusToYear}
                    onChange={(e) => setFormBonusToYear(parseInt(e.target.value) || "")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Bonus Amount (Rs.) *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    required
                    placeholder="Enter amount"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    inputProps={{ min: "1" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Payment Date *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Remarks
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="e.g. Diwali Bonus 2026"
                    value={formRemarks}
                    onChange={(e) => setFormRemarks(e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenAddDialog(false)} color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editRecordId ? "Update Payment" : "Save Payment"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default YearlyBonus;
